import { useEffect, useState } from 'react';

import { RouteLink } from '../studies/RouteLink.tsx';
import type { Route } from '../studies/routing.ts';
import { failureMessage } from './failure.ts';

export interface CheckEmailNoticeProps {
  /** The address as the user typed it. */
  email: string;
  /** Asks for the same message again; it answers exactly as the first request did. */
  sendAgain: (email: string) => Promise<void>;
  onNavigate: (route: Route) => void;
  /**
   * How long "send it again" waits after the request that led here, and after each repeat that
   * went through. Unset, it can be pressed at once.
   */
  holdAgainMs?: number;
}

/**
 * What every request for account mail ends on: a signup, a resend and a reset request alike.
 *
 * It says the same thing whatever the address turned out to be — new, waiting for confirmation or
 * long confirmed — because the server's answer is the same for all three and this screen is built
 * from nothing else. Anything that varied here would tell a stranger which addresses have accounts.
 */
export const CheckEmailNotice = ({
  email,
  sendAgain,
  onNavigate,
  holdAgainMs,
}: CheckEmailNoticeProps) => {
  const [pending, setPending] = useState(false);
  const [sentAgain, setSentAgain] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Counts the requests that went through, each of which starts the hold again. */
  const [sends, setSends] = useState(0);
  const [holding, setHolding] = useState(holdAgainMs !== undefined);

  useEffect(() => {
    if (holdAgainMs === undefined) {
      return;
    }

    setHolding(true);
    const release = setTimeout(() => setHolding(false), holdAgainMs);

    return () => clearTimeout(release);
  }, [holdAgainMs, sends]);

  return (
    <div data-test-id="check-email">
      <h1>Check your e-mail</h1>
      <p data-test-id="check-email-message">
        We have sent a message to <strong data-test-id="check-email-address">{email}</strong>. Open
        the link in it to carry on.
      </p>
      <p className="login-note">
        Nothing there after a few minutes? Check your spam folder, or send it again.
      </p>

      {error === null ? null : (
        <p className="login-error" data-test-id="check-email-error" role="alert">
          {error}
        </p>
      )}
      {holding ? (
        <p className="login-note" data-test-id="check-email-hold">
          You can ask for it again in a minute.
        </p>
      ) : null}
      {sentAgain ? (
        <p className="login-note" data-test-id="check-email-sent-again" role="status">
          Sent again.
        </p>
      ) : null}

      <button
        type="button"
        data-test-id="check-email-send-again"
        disabled={pending || holding}
        onClick={() => {
          setPending(true);
          setSentAgain(false);
          setError(null);
          void sendAgain(email).then(
            () => {
              setPending(false);
              setSentAgain(true);
              setSends((count) => count + 1);
            },
            (failure: unknown) => {
              setPending(false);
              setError(failureMessage(failure));
            },
          );
        }}
      >
        {pending ? 'Sending…' : 'Send it again'}
      </button>

      <p className="login-links">
        <RouteLink to={{ name: 'studies' }} onNavigate={onNavigate} testId="check-email-sign-in">
          Back to sign in
        </RouteLink>
      </p>
    </div>
  );
};
